'use client';
import { createContext, useContext, useState } from 'react';
import { Dialog } from '@/components/shared/dialog';

const DialogContext = createContext({
  addDialog: () => {},
  confirm: () => {}
});

export function useDialogContext() {
  return useContext(DialogContext);
}

export function useDialog({ title, content, actions }) {
  const { addDialog } = useContext(DialogContext);
  const open = () => {
    addDialog({ title, content, actions });
  };

  return open;
}

export function useConfirm() {
  const { addDialog } = useContext(DialogContext);

  return (
    title,
    content,
    confirmText = 'Ok',
    destructive = false,
    cancelText = 'Cancel'
  ) => {
    let deferred;
    const result = new Promise((resolve) => {
      deferred = resolve;
    });

    if (!content) {
      content = title;
      title = null;
    }

    addDialog({
      title,
      content,
      onClose: () => {
        if (deferred) {
          const fn = deferred;
          deferred = null;
          fn(false);
        }
      },
      actions: [
        {
          label: cancelText,
          cancel: true
        },
        {
          label: confirmText,
          destructive,
          onClick: () => {
            if (deferred) {
              const fn = deferred;
              deferred = null;
              fn(true);
            }
          }
        }
      ]
    });

    return result;
  };
}

export default function DialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);

  const addDialog = (dialogOpts) =>
    setDialogs((dialogs) => [...dialogs, dialogOpts]);

  const removeDialog = (index) => {
    if (dialogs?.[index]?.onClose) {
      dialogs[index].onClose();
    }
    // Tailwind modal has a transition time of 200ms
    // Adjust this if that changes
    setTimeout(() => {
      dialogs.splice(index, 1);
      setDialogs([...dialogs]);
    }, 200);
  };

  return (
    <DialogContext.Provider value={{ addDialog }}>
      {children}
      {dialogs.map((dialog, i) => (
        <Dialog {...dialog} key={i} onClose={() => removeDialog(i)} />
      ))}
    </DialogContext.Provider>
  );
}
